import { AbsgArrayHelper, EnvironmentConfiguration, LogService } from '@absg/angular-common';
import { ODataResult } from '@absg/angular-components';
import { PatientRequest, PatientsApi as PartyPatientApi, UpdatePatientDemograpics } from '@absg/api-party';
import {
  CreatePatient, CreatePatientAllergy, CreatePatientDiagnosis, CreatePatientRequest, CreatePatientResponse,
  PatientsApi, UpdateDiagnosis, UpdatePatientAllergies, UpdatePatientDiagnoses,
} from '@absg/api-patient';
import { BaseService, Patient, PatientDemographics } from '@absg/nim-client-shared';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DiagnosisCode } from 'shared.models/diagnosis-code.model';
import { DiagnosisInclusion } from 'shared.root/models/diagnosis-inclusion.mode';
import { DisplayAllergy } from 'shared.root/models/display-allergy.model';
import { PatientHealthResponse } from 'shared.root/models/patient-health';

@Injectable({ providedIn: 'root' })
export class PatientService extends BaseService {
  constructor(private http: HttpClient,
    private environment: EnvironmentConfiguration,
    private patientsApi: PatientsApi,
    private partyPatientApi: PartyPatientApi,
    loggingService: LogService,
  ) {
    super(loggingService);
  }

  getPatients(query = ''): Observable<Patient[]> {
    const params = new HttpParams({ fromString: query });

    const requestOptions = {
      params,
    };

    const results = this.http.get<ODataResult<Patient>>(this.environment.apis.patient.endpointUrl, requestOptions)
      .pipe(
        map((response) => this.sortList(response)),
        // TODO: Remove this map function when data is being pulled from a real api
        map((patients: Patient[]) => patients.filter((patient: Patient) => {
          let isMatch = false;

          // Ensure the dateOfBirth is an actual Date object; the date returned from the current service is only a string
          patient.dateOfBirth = new Date(patient.dateOfBirth);

          if (query.length === 0) {
            // Return each and every patient if no search term is entered
            isMatch = true;
          } else {
            // Replace `/` and `-` with space
            const searchTerms = query.replace('/', ' ').replace('-', ' ').split(' ');

            // Check if any of the search terms match the searchable fields
            searchTerms.forEach(term => {
              isMatch = isMatch || (term.length > 0 && (
                patient.firstName.toLowerCase().includes(term.toLowerCase()) ||
                patient.lastName.toLowerCase().includes(term.toLowerCase()) ||
                patient.patientId.toLowerCase().includes(term.toLowerCase()) ||
                patient.dateOfBirth.getDate().toString().includes(term) ||
                (patient.dateOfBirth.getMonth() + 1).toString().includes(term) ||
                patient.dateOfBirth.getFullYear().toString().includes(term)));
            });
          }

          return isMatch;
        })),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)));

    return results;
  }

  sortList(response: ODataResult<Patient>): Patient[] {
    const result = response.value as Patient[];
    return result.sort((p1, p2) => p1.lastName < p2.lastName ? -1 : 1);
  }

  getPatientDiagnosisById(patientId: number): Observable<DiagnosisCode[]> {
    return this.patientsApi.getPatientById(patientId)
      .pipe(
        map(response => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const responseData = response as any;
          if (responseData && responseData.Diagnoses) {
            return this.convertDataModel(responseData.Diagnoses);
          } else {
            return null;
          }
        }),
        catchError((response: HttpErrorResponse) => {
          const resp = this.handleErrorResponse(response);
          if (resp != null) {
            return resp;
          }

          return of([]);
        }),
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  convertDataModel(diagnosisCodes: any[]): DiagnosisCode[] {
    const diagnosisCodeArray = new Array<DiagnosisCode>();
    diagnosisCodes.forEach(diagnosisCode => {
      const localDiagnosisCode =
        {
          code: diagnosisCode.Code,
        } as DiagnosisCode;
      diagnosisCodeArray.push(localDiagnosisCode);
    });
    return diagnosisCodeArray;
  }

  createPatient(patient: PatientDemographics, allergies: DisplayAllergy[], diagnosis: DiagnosisCode[]): Observable<PatientDemographics> {
    const request = this.createSavePatientRequest(patient, allergies, diagnosis);

    return this.patientsApi.createPatient(request)
      .pipe(
        catchError((response) => this.handleErrorResponse(response)),
        map((response: CreatePatientResponse) => this.mapSavePatientResponse(response)),
      );
  }

  updatePatientDemographics(patientDemographics: PatientDemographics): Observable<boolean> {
    const update = {
      PatientId: patientDemographics.patientId,
      IdNumber: patientDemographics.idNumber,
      AlternateIdNumber: patientDemographics.alternateIdNumber,
      Title: '',
      FirstName: patientDemographics.firstName,
      LastName: patientDemographics.lastName,
      MiddleName: patientDemographics.middleName,
      GenderType: patientDemographics.genderType,
      BirthDate: patientDemographics.birthDate,
      ClinicPartyId: patientDemographics.clinicId,
      AdmittingPhysicianId: patientDemographics.admittingPhysicianId,
      admissionDate: patientDemographics.admissionDate,
    } as UpdatePatientDemograpics;
    return this.partyPatientApi.updatePatientAndAdmissionDetails(update)
      .pipe(
        catchError((response) => this.handleErrorResponse(response)),
      );
  }

  updatePatientAllergies(patientId: number, allergies: DisplayAllergy[]): Observable<boolean> {
    const allergyIds = AbsgArrayHelper.map(allergies, (x) => ({ listInclusionId: x.listInclusionId }));
    const updatePatientAllergies = {
      patientId,
      allergies: allergyIds,
    } as UpdatePatientAllergies;

    return this.patientsApi.updatePatientAllergies(updatePatientAllergies)
      .pipe(
        catchError((response) => this.handleErrorResponse(response)),
      );
  }

  updatePatientDiagnoses(patientId: number, diagnosis: DiagnosisCode[]): Observable<boolean> {
    let sequence = 0;

    const diagnosisCodes = AbsgArrayHelper.map(diagnosis, (x) => ({
      code: x.code,
      sequence: sequence++,
    } as UpdateDiagnosis));

    const dx = {
      patientId,
      diagnoses: diagnosisCodes,
    } as UpdatePatientDiagnoses;

    return this.patientsApi.updatePatientDiagnoses(dx)
      .pipe(
        catchError((response) => this.handleErrorResponse(response)),
      );
  }

  getPatientHealthById(patientId: number): Observable<PatientHealthResponse> {
    return this.patientsApi.getPatientById(patientId)
      .pipe(
        map(response => this.mapPatientHealthResponse(response)),
        catchError((response) => this.handleErrorResponse(response)),
      );
  }

  getPatientsSearchData(patientRequest: PatientRequest): Observable<PatientDemographics[]> {
    return this.partyPatientApi.getPatientsBySearchRequest(patientRequest)
      .pipe(
        map(response => this.mapPatientSearchRequestResponse(response)),
        catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPatientSearchRequestResponse(response: any): PatientDemographics[] {
    if (!response) {
      return null;
    }
    let searchPatients: PatientDemographics[] = [];
    searchPatients = AbsgArrayHelper.map(response, item => {
      const searchPatientData: PatientDemographics = {
        patientId: item.PatientId,
        idNumber: item.IdNumber,
        title: '',
        firstName: item.FirstName,
        lastName: item.LastName,
        middleName: item.MiddleName,
        genderType: Number(item.GenderType),
        birthDate: item.BirthDate,
        admissionDate: item.AdmissionDate,
        alternateIdNumber: '',
        height: '',
        weight: '',
        clinicId: 0,
        admittingPhysicianId: 0,
        lastDispenseDate: item.LastDispenseDate,
        partyPatientId: item.PatientPartyId,
      };
      return searchPatientData;
    });
    return searchPatients;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPatientHealthResponse(response: any): PatientHealthResponse {
    if (!response) {
      return null;
    }
    const patientHealth = new PatientHealthResponse();

    patientHealth.allergies = this.mapPatientHealthAllergiesResponse(response.Allergies);
    patientHealth.diagnoses = this.mapPatientHealthDiagnosesResponse(response.Diagnoses);

    return patientHealth;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPatientHealthAllergiesResponse(allergies: any): number[] {
    if (!allergies) {
      return null;
    }

    let inclusionIds: number[] = [];
    inclusionIds = AbsgArrayHelper.map(allergies, x => x.ListInclusionId);

    return inclusionIds;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPatientHealthDiagnosesResponse(diagnoses: any): DiagnosisInclusion[] {
    if (!diagnoses) {
      return null;
    }

    let inclusionIds: DiagnosisInclusion[] = [];
    inclusionIds = AbsgArrayHelper.map(diagnoses, x => {
      const inclusion: DiagnosisInclusion = {
        code: x.Code,
        sequence: x.Sequence,
      };
      return inclusion;
    });

    return inclusionIds;
  }

  private createSavePatientRequest(patient: PatientDemographics, allergies: DisplayAllergy[], diagnosis: DiagnosisCode[]): CreatePatientRequest {
    const request: CreatePatientRequest = {
      partyCreatePatient: this.mapPatient(patient),
      createPatientHealth: {
        allergies: this.mapAllergies(allergies),
        diagnoses: this.mapDiagnoses(diagnosis),
      },
    };

    return request;
  }

  private mapPatient(patientDemographics: PatientDemographics): CreatePatient {
    const patient: CreatePatient = {};

    patient.idNumber = patientDemographics.idNumber;
    patient.alternateIdNumber = patientDemographics.alternateIdNumber;
    patient.admittingPhysicianId = patientDemographics.admittingPhysicianId;
    patient.firstName = patientDemographics.firstName;
    patient.lastName = patientDemographics.lastName;
    patient.birthDate = patientDemographics.birthDate;
    patient.admissionDate = patientDemographics.admissionDate;
    patient.genderType = CreatePatient.GenderTypeEnum[PatientDemographics.getPatientSexById(patientDemographics.genderType)];

    return patient;
  }

  private mapAllergies(allergies: DisplayAllergy[]): CreatePatientAllergy[] {
    if (!allergies) {
      return null;
    }

    let patientAllergies: CreatePatientAllergy[] = [];
    patientAllergies = AbsgArrayHelper.map(allergies, x => {
      const allergy: CreatePatientAllergy = {
        listInclusionId: x.listInclusionId,
      };
      return allergy;
    });

    return patientAllergies;
  }

  private mapDiagnoses(patientDiagnoses: DiagnosisCode[]): CreatePatientDiagnosis[] {
    if (!patientDiagnoses || patientDiagnoses?.length === 0) {
      return null;
    }

    let diagnosis: CreatePatientDiagnosis[] = [];
    diagnosis = AbsgArrayHelper.map(patientDiagnoses, (x, index) => {
      const diagnosisCode: CreatePatientDiagnosis = {
        code: x.code,
        sequence: index,
      };
      return diagnosisCode;
    });

    return diagnosis;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapSavePatientResponse(response: any): PatientDemographics {
    if (!response) {
      return null;
    }

    const patientSaveResponse = response.PartyPatientResult;
    const patient = new PatientDemographics();
    patient.patientId = patientSaveResponse.PatientId;
    patient.idNumber = patientSaveResponse.IdNumber;
    patient.firstName = patientSaveResponse.FirstName;
    patient.lastName = patientSaveResponse.LastName;
    if (!!patientSaveResponse.BirthDate) {
      patient.birthDate = patientSaveResponse.BirthDate;
    }
    patient.alternateIdNumber = patientSaveResponse.AlternateIdNumber;
    if (!!patientSaveResponse.AdmissionDate) {
      patient.admissionDate = patientSaveResponse.AdmissionDate;
    }
    patient.admittingPhysicianId = patientSaveResponse.AdmittingPhysicianId;

    patient.genderType = patientSaveResponse.GenderType;
    // TODO: remove this code if its not required while working on patient edit story
    //patient.clinicId = response.ClinicPartyId;
    // if (!!patientSaveResponse.lastDispenseDate) {
    //   patient.lastDispenseDate = DateTimeHelper.convertUtcDateToLocalDate(new Date(patientSaveResponse.lastDispenseDate));
    // }

    return patient;
  }
}
